import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
  CloudArrowUp,
  Eye,
  EyeSlash,
  FileText,
  Info,
  X,
} from '@phosphor-icons/react';
import { Accept, useDropzone } from 'react-dropzone';
import { AccentButton } from '../../../shared/Buttons';
import useApi from '../../../utils/AxiosWrapper';
import { HttpStatusCode } from 'axios';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../utils/ScreenViewContext';
import { Application } from '../../../types/dbTypes';
import toast from 'react-hot-toast';
import { formatDate } from '../../../utils/DateUtils';
import { ApplicationStatus, ApplicationStep } from '../../../types/apiTypes';
import { setApplicationStep } from '../../../pages/signup';
import { useUser } from '../../../utils/UserContext';
import { safeSessionSetItem } from '../../../shared/safeStorage';
import {
  MAX_REQUEST_UPLOAD_BYTES,
  MAX_REQUEST_UPLOAD_MB,
} from '../../../types/constants';
import {
  base64ByteLength,
  classifyPdf,
  combineFilesToPDF,
  DocumentMergeError,
  exceedsUploadCap,
  isPdfFile,
  PdfStatus,
  readFileBytes,
  reportUploadBlocked,
  reportUploadCrashed,
} from './documentUploadGuards';

enum DocumentRequestStatus {
  PENDING = 'pending',
  SUBMITTED = 'submitted',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  RE_REQUESTED = 're_requested',
}

export interface RequestDocument {
  accepted_at: string | null;
  ancillary_owner_id: string | null;
  application_id: string;
  customer_id: string;
  date_requested: string;
  document_request_id: string;
  document_type: string;
  driver_id: string | null;
  re_requested_at: string | null;
  rejected_at: string | null;
  status: string;
  submitted: boolean;
  submitted_at: string | null;
  display_name: string;
  display_subtext: string;
  // THT-703: true when this is the onboarding "Submit Bank Statements" request. Optional — only
  // present once the requested-documents endpoint (arch #1496) ships; treat missing as false.
  is_onboarding_revenue_verification?: boolean;
}

type DocumentUpload = {
  insuranceDocs: FileList;
  revenueDocs: FileList;
};

const formatText = (text: string) =>
  text.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const formatBytes = (bytes: number) => {
  if (!bytes && bytes !== 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0,
    n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const truncateFilename = (filename: string, maxLength: number = 30) => {
  if (filename.length <= maxLength) return filename;

  // Try to preserve the file extension
  const lastDotIndex = filename.lastIndexOf('.');
  const extension = lastDotIndex !== -1 ? filename.slice(lastDotIndex) : '';
  const nameWithoutExt =
    lastDotIndex !== -1 ? filename.slice(0, lastDotIndex) : filename;

  // Calculate how much of the name we can show
  const availableLength = maxLength - extension.length - 3; // 3 for "..."

  if (availableLength <= 0) {
    // If extension is too long, just truncate the whole thing
    return filename.slice(0, maxLength - 3) + '...';
  }

  return nameWithoutExt.slice(0, availableLength) + '...' + extension;
};

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = (reader.result as string)?.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });

/** Encodes a decrypted PDF re-save (never a File/Blob, so `toBase64` doesn't apply). */
const bytesToBase64 = (bytes: Uint8Array): string => {
  // Chunked so a re-saved PDF doesn't blow the call stack via `apply`.
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
};

/**
 * Identifies a selected file across re-renders and re-orderings, so its PDF
 * classification and (if it needs one) its password stay attached to it when
 * other files are added or removed. Scoped by document type: the same file can
 * be attached to two requests, and removing it from one must not wipe the
 * password typed for the other.
 */
const fileKey = (docType: string, file: File): string =>
  `${docType}|${file.name}|${file.size}|${file.lastModified}`;

function DocumentUploadForm({
  application_id,
  setStep,
  application,
  title,
  description,
}: {
  application_id: string;
  setStep: (step: string) => void;
  application: Application;
  // Optional header overrides so a caller can tailor the voice (e.g. onboarding bank statements)
  // without this component knowing anything about the specific request type. Defaults keep the
  // generic "additional documents" copy used for admin/compliance requests.
  title?: string;
  description?: string;
}) {
  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<DocumentUpload>();

  const { callProtectedApi } = useApi();

  const acceptFiles: Accept = {
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpeg', '.jpg'],
  };

  const onSubmit: SubmitHandler<DocumentUpload> = (data) => {
    safeSessionSetItem('documentUploadInfo', JSON.stringify(data));
  };
  const { userId } = useUser();
  const { screenView } = useScreenView();
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [localDocs, setLocalDocs] = useState<Record<string, File[]>>({});
  const [requestedDocuments, setRequestedDocuments] = useState<
    RequestDocument[]
  >([]);

  const [loading, setLoading] = useState(false);

  // Pre-flight result per selected PDF, keyed by `fileKey`. Only files that are
  // about to be merged are classified — a single file uploads as-is, protected
  // or not, so there is nothing to decrypt and nothing to ask for.
  const [pdfStatuses, setPdfStatuses] = useState<Record<string, PdfStatus>>({});
  // Passwords the customer typed for `needs_password` files. They live here for
  // the duration of the upload only: used to build the merge, cleared on
  // success, never persisted and never part of the request or a Sentry event.
  const [filePasswords, setFilePasswords] = useState<Record<string, string>>(
    {}
  );

  // This is criteria for the upload to be shown
  const needsUpload = (status: DocumentRequestStatus) => {
    return (
      status === DocumentRequestStatus.PENDING ||
      status === DocumentRequestStatus.RE_REQUESTED
    );
  };

  const hasValidUploads = useMemo(() => {
    return requestedDocuments
      .filter((doc) => needsUpload(doc.status as DocumentRequestStatus))
      .every((doc) => {
        const files = localDocs[doc.document_type];
        return files && files.length > 0;
      });
  }, [localDocs, requestedDocuments]);

  // PDFs that have not been classified yet — a lone staged PDF is classified
  // exactly like one that is about to be merged, so a single-file upload never
  // ships still-encrypted bytes.
  const pendingClassification = useMemo(
    () =>
      Object.entries(localDocs).flatMap(([docType, files]) =>
        (files ?? [])
          .filter(
            (file) =>
              isPdfFile(file) &&
              pdfStatuses[fileKey(docType, file)] === undefined
          )
          .map((file) => [docType, file] as const)
      ),
    [localDocs, pdfStatuses]
  );

  // Classify as soon as a file is selected, so the password field appears
  // before the customer hits Send rather than after a failed upload.
  useEffect(() => {
    if (!pendingClassification.length) return;
    let cancelled = false;

    const classifyPending = async () => {
      const classified = await Promise.all(
        pendingClassification.map(
          async ([docType, file]): Promise<[string, PdfStatus]> => {
            try {
              const { status } = await classifyPdf(await readFileBytes(file));
              return [fileKey(docType, file), status];
            } catch {
              return [fileKey(docType, file), 'unreadable'];
            }
          }
        )
      );

      if (cancelled) return;
      setPdfStatuses((prev) => ({
        ...prev,
        ...Object.fromEntries(classified),
      }));
    };

    void classifyPending();

    return () => {
      cancelled = true;
    };
  }, [pendingClassification]);

  // A file that needs a password cannot be merged or uploaded without one, so
  // Send stays disabled until every one of them has something typed in.
  const isAwaitingPassword = useMemo(
    () =>
      Object.entries(localDocs).some(([docType, files]) =>
        (files ?? []).some(
          (file) =>
            pdfStatuses[fileKey(docType, file)] === 'needs_password' &&
            !filePasswords[fileKey(docType, file)]?.length
        )
      ),
    [localDocs, pdfStatuses, filePasswords]
  );

  const handlePasswordChange = (
    docType: string,
    file: File,
    password: string
  ) => {
    setFilePasswords((prev) => ({
      ...prev,
      [fileKey(docType, file)]: password,
    }));
  };

  const forgetFiles = (docType: string, files: File[]) => {
    if (!files.length) return;
    const keys = new Set(files.map((file) => fileKey(docType, file)));
    const without = <T,>(record: Record<string, T>) =>
      Object.fromEntries(
        Object.entries(record).filter(([key]) => !keys.has(key))
      );
    setFilePasswords((prev) => without(prev));
    setPdfStatuses((prev) => without(prev));
  };

  const fetchRequestedDocuments = useCallback(
    async (application_id: string) => {
      setLoadingDocuments(true);
      try {
        const response = await callProtectedApi(
          `/applications/${application_id}/requested-documents`,
          'get',
          {},
          'Error occurred while fetching requested documents.'
        );

        const data = response?.data?.data?.documents.document_requests;
        if (
          data &&
          data?.length > 0 &&
          response?.status === HttpStatusCode.Ok
        ) {
          setRequestedDocuments(data);
        } else {
          toast.error('No required documents found.');
        }
      } catch (e: any) {
        console.error('Error occurred while fetching linked accounts: ', e);
      } finally {
        setLoadingDocuments(false);
      }
    },
    [callProtectedApi]
  );

  useEffect(() => {
    if (!application_id.length) return;
    const getRequestedDocuments = async () => {
      try {
        setLoadingDocuments(true);

        await fetchRequestedDocuments(application_id);
      } finally {
        setLoadingDocuments(false);
      }
    };
    getRequestedDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application_id]);

  const handleClear = (fieldName: keyof DocumentUpload) => {
    forgetFiles(fieldName, localDocs[fieldName] || []);
    setLocalDocs((prev) => ({ ...prev, [fieldName]: [] }));
    setValue(fieldName, [] as any);
  };

  const handleRemoveFile = (fieldName: keyof DocumentUpload, index: number) => {
    const removed = (localDocs[fieldName] || [])[index];
    if (removed) forgetFiles(fieldName, [removed]);
    setLocalDocs((prev) => {
      const files = prev[fieldName] || [];
      const newFiles = files.filter((_, i) => i !== index);
      return {
        ...prev,
        [fieldName]: newFiles,
      };
    });
  };

  const handleDrop = (
    acceptedFiles: File[],
    fieldName: keyof DocumentUpload
  ) => {
    // Validate total size before accepting files
    setLocalDocs((prev) => {
      if (!acceptedFiles.length) return prev;

      // Get existing files for this field
      const existingFiles = prev[fieldName] || [];
      const allFiles = [...existingFiles, ...acceptedFiles];

      // Check total size (per document type)
      const totalSize = allFiles.reduce((sum, file) => sum + file.size, 0);
      const totalSizeMB = totalSize / (1024 * 1024);

      if (exceedsUploadCap(totalSize)) {
        toast.error(
          `Total size would exceed ${MAX_REQUEST_UPLOAD_MB}MB limit. Current: ${totalSizeMB.toFixed(2)}MB`
        );
        return prev;
      }

      return {
        ...prev,
        [fieldName]: allFiles, // Add to existing files instead of replacing
      };
    });

    setValue(fieldName, acceptedFiles as any);
  };

  const handleSetLoading = (loadingBool: boolean) => {
    setLoading(loadingBool);
  };

  const handleUpload = async () => {
    setLoading(true);

    try {
      for (const [docTypeKey, files] of Object.entries(localDocs)) {
        if (!files || files.length === 0) continue;

        // Check total size for all files in this document type
        const inputBytes = files.reduce((sum, file) => sum + file.size, 0);
        if (exceedsUploadCap(inputBytes)) {
          toast.error(
            `Total size for ${docTypeKey} exceeds ${MAX_REQUEST_UPLOAD_MB}MB limit`
          );
          setLoading(false);
          return;
        }

        // Validate file extensions
        for (const file of files) {
          const extension = file.name?.split('.').pop()?.toLowerCase();
          const validExts = ['pdf', 'jpg', 'jpeg', 'png'];
          if (!extension || !validExts.includes(extension)) {
            toast.error(
              `"${truncateFilename(file.name)}" has invalid extension. Only PDF, JPG, and PNG files are allowed.`
            );
            setLoading(false);
            return;
          }
        }

        // Find matching document request
        const matchingRequest = requestedDocuments.find(
          (req) => req.document_type.toLowerCase() === docTypeKey.toLowerCase()
        );

        if (!matchingRequest) {
          toast.error(`No pending request for type "${docTypeKey}"`);
          setLoading(false);
          return;
        }

        const { document_request_id } = matchingRequest;

        let base64: string;
        let fileName: string;

        if (files.length === 1 && !isPdfFile(files[0])) {
          // Single non-PDF file (JPG/PNG) - upload as-is, unaffected by any of
          // this: no classification, no re-encoding.
          base64 = await toBase64(files[0]);
          fileName = files[0].name;
        } else {
          fileName =
            files.length === 1
              ? files[0].name
              : `combined_${docTypeKey}_${files.length}_files.pdf`;

          try {
            if (files.length === 1) {
              // Single PDF - classified and re-saved decrypted, exactly like a
              // file that is about to be merged, so the backend (and the admin
              // review screen) never receives ciphertext.
              const file = files[0];
              const { status, doc } = await classifyPdf(
                await readFileBytes(file),
                filePasswords[fileKey(docTypeKey, file)]
              );

              if (status !== 'ok' || !doc) {
                throw new DocumentMergeError(
                  status === 'ok' ? 'unreadable' : status,
                  file.name
                );
              }

              base64 = bytesToBase64(await doc.save());
            } else {
              // Multiple files - combine them into a single PDF
              base64 = await combineFilesToPDF(
                files,
                (file) => filePasswords[fileKey(docTypeKey, file)]
              );
            }
          } catch (error) {
            // Every blocked merge or single-file classification names the
            // file and says what to do about it.
            const blocked =
              error instanceof DocumentMergeError ? error : undefined;
            const blockedFile = truncateFilename(blocked?.fileName ?? '');

            if (blocked?.status === 'wrong_password') {
              toast.error(
                `The password for ${blockedFile} is incorrect. Try again, or upload this file on its own.`
              );
            } else if (blocked?.status === 'needs_password') {
              toast.error(
                `${blockedFile} needs its password before it can be combined. Enter it under the file and try again.`
              );
            } else if (blocked) {
              toast.error(
                `${blockedFile} couldn't be read as a PDF. Re-export or re-scan it and try again.`
              );
            } else {
              console.error('Error combining documents:', error);
              toast.error(
                "We couldn't combine these files. Try uploading them one at a time."
              );
            }

            const context = {
              documentType: docTypeKey,
              fileCount: files.length,
              inputBytes,
            };

            if (blocked) {
              reportUploadBlocked({
                ...context,
                reason:
                  blocked.status === 'unreadable'
                    ? 'unreadable_pdf'
                    : blocked.status,
              });
            } else {
              // Not a blocked case — an actual crash, worth its stack.
              reportUploadCrashed(error, context);
            }

            setLoading(false);
            return;
          }
        }

        // The artifact we are about to send is what has to fit the cap: the
        // merge can come out bigger than any single input, and base64 inflates
        // it another ~33% inside the JSON body.
        const artifactBytes = base64ByteLength(base64);
        if (exceedsUploadCap(artifactBytes)) {
          toast.error(
            `The combined file is ${(artifactBytes / (1024 * 1024)).toFixed(1)} MB, over the ${MAX_REQUEST_UPLOAD_MB} MB limit. Upload fewer files at a time.`
          );
          reportUploadBlocked({
            reason: 'combined_too_large',
            documentType: docTypeKey,
            fileCount: files.length,
            inputBytes,
            outputBytes: artifactBytes,
          });
          setLoading(false);
          return;
        }

        // Upload the file (single file or combined PDF)
        const res = await callProtectedApi(
          `/applications/${application_id}/requested-documents/${document_request_id}/upload`,
          'post',
          {
            file: base64,
            file_name: fileName,
          },
          `Error uploading ${fileName}`
        );

        if (res?.status !== HttpStatusCode.Ok) {
          toast.error(`Failed to upload ${truncateFilename(fileName)}`);
          setLoading(false);
          return;
        }
      }

      // Every document type is uploaded: the passwords have done their job.
      setFilePasswords({});
      setPdfStatuses({});

      const step = await setApplicationStep({
        step: ApplicationStep.PendingReview,
        application_id,
        customer_id: userId ?? '',
        callProtectedApi,
        setLoading: handleSetLoading,
        previousStep: ApplicationStep.ManualDocumentUpload,
        applicationStatus: application?.status as ApplicationStatus,
      });

      if (step === ApplicationStep.PendingReview) {
        setStep(ApplicationStep.PendingReview);
      }

      toast.success('All uploads completed');
    } catch (e) {
      toast.error('Unexpected upload error');
    } finally {
      setLoading(false);
      void fetchRequestedDocuments(application_id);
    }
  };

  return (
    <div
      className={`flex w-full ${screenView === 'desktop' ? 'h-screen min-h-screen flex-row' : 'h-full min-h-screen flex-col overflow-y-auto overflow-x-hidden'} flex-grow items-center justify-between align-middle`}
    >
      {!loading && !loadingDocuments ? (
        <>
          <div
            className={`flex ${screenView === 'desktop' ? 'fixed left-0 top-0 min-h-full w-[50%] max-w-[50%] flex-1 flex-grow px-[80px]' : 'h-full w-full px-[16px]'} flex-col items-start gap-y-[64px] bg-primarybg py-[48px]`}
          >
            <img alt="tach-logo" src="/TachLogo.svg" className="w-[150px]" />
            <h1 className="text-[32px] font-[700] text-primary">
              {title ?? 'Additional Information Required for Verification'}
            </h1>
            <p className="text-[16px] text-primary">
              {description ??
                'To ensure the security and accuracy of your financial information, we occasionally require additional documentation or information. Verifying your identity and financial status helps us provide the best possible support and tailor services to your needs.'}
            </p>
          </div>
          <div
            className={`flex h-full ${screenView === 'desktop' ? 'ml-[50%] w-[50%] max-w-[50%] overflow-y-auto' : 'w-full overflow-visible'} flex-col bg-white pt-[48px]`}
          >
            <form
              onSubmit={handleSubmit(onSubmit)}
              className={`flex h-full w-full flex-col items-center gap-y-[16px] px-[48px] max-lg:px-[16px]`}
            >
              <div className="flex w-full flex-col gap-y-[8px]">
                <h2 className="text-[22px] font-[700] text-primary">
                  Upload Required Documents
                </h2>
                <p className="text-[16px] text-graysecondary">
                  Please submit the requested documents to complete the
                  verification process.{' '}
                </p>
              </div>

              {requestedDocuments.map((doc) => {
                const fieldName = doc.document_type as keyof DocumentUpload;
                const uploadedFiles = localDocs[doc.document_type] || [];

                const isDisabled =
                  doc.status !== DocumentRequestStatus.PENDING &&
                  doc.status !== DocumentRequestStatus.RE_REQUESTED;

                return (
                  <CustomDropzone
                    key={doc.document_request_id}
                    onDrop={(files) => handleDrop(files, fieldName)}
                    accept={acceptFiles}
                    label={doc.display_name}
                    subtext={doc.display_subtext}
                    onClear={() => handleClear(fieldName)}
                    onRemoveFile={(index) => handleRemoveFile(fieldName, index)}
                    errorMessage={
                      errors[fieldName]
                        ? `${doc.document_type} Documents are required`
                        : ''
                    }
                    uploadedFiles={uploadedFiles}
                    disabled={isDisabled}
                    status={doc.status}
                    submitted_at={doc.submitted_at}
                    needsUpload={needsUpload}
                    pdfStatusOf={(file) =>
                      pdfStatuses[fileKey(doc.document_type, file)]
                    }
                    passwordOf={(file) =>
                      filePasswords[fileKey(doc.document_type, file)] ?? ''
                    }
                    onPasswordChange={(file, password) =>
                      handlePasswordChange(doc.document_type, file, password)
                    }
                  />
                );
              })}

              <div className="flex flex-row items-center justify-evenly space-x-1 align-middle text-[#7D8191]">
                <Info size={20} className="w-[8%]" />
                <p className="text-[14px]">
                  Ensure the documents are clear and legible to facilitate a
                  smooth verification process. This helps us complete your
                  verification quickly and accurately.
                </p>
              </div>
              <div className="mt-4 flex flex-col items-center justify-center space-y-[16px] pb-[100px] align-middle">
                <AccentButton
                  disabled={
                    !hasValidUploads ||
                    isAwaitingPassword ||
                    pendingClassification.length > 0 ||
                    loading
                  }
                  onClick={() => handleUpload()}
                  addClass="rounded-full w-[250px] h-[48px]"
                >
                  Send
                </AccentButton>
              </div>
            </form>
          </div>{' '}
        </>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <ClipLoader
            color="#1D213B"
            loading={loading || loadingDocuments}
            size={60}
            aria-label="Loading Spinner"
            data-testid="loader"
            className="flex h-full w-full items-center justify-center"
          />
          {!loadingDocuments && (
            <h2 className="text-[16px] text-primary">
              {' '}
              Uploading Documents...
            </h2>
          )}
        </div>
      )}
    </div>
  );
}

export default DocumentUploadForm;

function CustomDropzone({
  onDrop,
  onClear,
  onRemoveFile,
  needsUpload,
  accept,
  label,
  subtext,
  errorMessage,
  uploadedFiles,
  disabled,
  status,
  submitted_at,
  pdfStatusOf,
  passwordOf,
  onPasswordChange,
}: {
  onDrop: (acceptedFiles: File[]) => void;
  onClear: () => void;
  onRemoveFile: (index: number) => void;
  needsUpload: (status: DocumentRequestStatus) => boolean;
  accept: Accept;
  label: string;
  subtext?: string;
  errorMessage?: string;
  uploadedFiles: File[];
  disabled?: boolean;
  status?: string;
  submitted_at?: string | null;
  pdfStatusOf: (file: File) => PdfStatus | undefined;
  passwordOf: (file: File) => string;
  onPasswordChange: (file: File, password: string) => void;
}) {
  const dropzoneId = useId();
  const [visiblePasswords, setVisiblePasswords] = useState<
    Record<number, boolean>
  >({});
  // The character just typed flashes in the clear for a moment, mobile-style,
  // then falls back to a bullet — the reveal timers below are keyed by the
  // same file index as visiblePasswords so each field masks independently.
  const [revealLastCharFor, setRevealLastCharFor] = useState<
    Record<number, boolean>
  >({});
  const revealTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>(
    {}
  );
  useEffect(() => {
    const timers = revealTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const handlePasswordInput = (
    index: number,
    file: File,
    previousValue: string,
    nextValue: string
  ) => {
    onPasswordChange(file, nextValue);
    if (revealTimers.current[index]) {
      clearTimeout(revealTimers.current[index]);
    }
    if (nextValue.length <= previousValue.length) {
      setRevealLastCharFor((prev) => ({ ...prev, [index]: false }));
      return;
    }
    setRevealLastCharFor((prev) => ({ ...prev, [index]: true }));
    revealTimers.current[index] = setTimeout(() => {
      setRevealLastCharFor((prev) => ({ ...prev, [index]: false }));
    }, 1000);
  };

  const maskedPassword = (index: number, value: string) => {
    if (visiblePasswords[index]) return value;
    if (revealLastCharFor[index] && value.length > 0) {
      return '•'.repeat(value.length - 1) + value.slice(-1);
    }
    return '•'.repeat(value.length);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    maxSize: MAX_REQUEST_UPLOAD_BYTES,
    multiple: true, // Enable multiple file selection
    disabled,
    onDropRejected: (rejectedFiles) => {
      // Show error messages for rejected files
      rejectedFiles.forEach((rejection) => {
        const { file, errors } = rejection;
        errors.forEach((error) => {
          if (error.code === 'file-too-large') {
            const fileSizeMB = file.size / (1024 * 1024);
            toast.error(
              `"${truncateFilename(file.name)}" is ${fileSizeMB.toFixed(2)}MB, which exceeds the ${MAX_REQUEST_UPLOAD_MB}MB limit`
            );
          } else if (error.code === 'file-invalid-type') {
            toast.error(
              `"${truncateFilename(file.name)}" is not a valid file type. Only PDF, JPG, and PNG files are allowed.`
            );
          } else {
            toast.error(
              `"${truncateFilename(file.name)}" was rejected: ${error.message}`
            );
          }
        });
      });
    },
  });

  const handleBrowse = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    open();
  };

  const statusColorMap: Record<string, string> = {
    pending: 'text-yellow-600 bg-yellow-100',
    re_requested: 'text-yellow-600 bg-yellow-100',
    submitted: 'text-blue-600 bg-blue-100',
    accepted: 'text-green-700 bg-green-100',
    rejected: 'text-red-700 bg-red-100',
    cancelled: 'text-gray-700 bg-gray-100',
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {subtext && <p className="text-xs text-gray-500">{subtext}</p>}

      {status && (
        <p
          className={`mt-1 inline-block rounded px-2 py-1 text-xs font-semibold ${statusColorMap[status] || ''}`}
        >
          Status: {formatText(status)}
        </p>
      )}

      {submitted_at && (
        <p className="mt-1 inline-block rounded px-2 py-1 text-xs">
          <strong>Submitted At:</strong>{' '}
          {formatDate(new Date(submitted_at ?? ''))}
        </p>
      )}

      {/* Only show upload area if status is pending or re_requested */}
      {needsUpload(status as DocumentRequestStatus) && (
        <>
          {!!uploadedFiles?.length ? (
            <div
              {...getRootProps()}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-gray-50 p-4"
              aria-live="polite"
            >
              <input {...getInputProps()} data-testid="document-upload-input" />
              <div className="space-y-2">
                {/* Display all uploaded files */}
                {uploadedFiles.map((file, index) => {
                  // Two document types can hold the same file at the same index,
                  // so the field id is scoped to this dropzone instance —
                  // otherwise both labels point at the first input.
                  const passwordFieldId = `pdf-password-${dropzoneId}-${index}`;

                  return (
                    <div key={index} className="flex flex-col">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-3">
                          <FileText
                            size={24}
                            className="shrink-0 text-[#8B919F]"
                          />
                          <div className="min-w-0">
                            <p
                              className="truncate text-sm font-medium text-primary"
                              title={file.name}
                            >
                              {file.name}
                            </p>
                            <p className="text-xs text-graysecondary">
                              {formatBytes(file.size)} • File {index + 1} of{' '}
                              {uploadedFiles.length}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onRemoveFile(index);
                          }}
                          className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
                          title="Remove this file"
                        >
                          <X size={14} />
                        </button>
                      </div>

                      {/* A PDF whose own password we could not guess: ask for it
                        here, next to the file it belongs to. Clicks and keys
                        stop here so typing does not re-open the file picker. */}
                      {pdfStatusOf(file) === 'needs_password' && (
                        <div
                          className="ml-[36px] mt-2 flex flex-col gap-1"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          onKeyDown={(e: React.KeyboardEvent) =>
                            e.stopPropagation()
                          }
                        >
                          <label
                            htmlFor={passwordFieldId}
                            className="text-xs font-medium text-primary"
                          >
                            This file needs its password to be merged
                          </label>
                          <div className="relative w-full max-w-[260px]">
                            <input
                              id={passwordFieldId}
                              type="text"
                              autoComplete="off"
                              autoCorrect="off"
                              autoCapitalize="off"
                              spellCheck={false}
                              data-lpignore="true"
                              data-1p-ignore="true"
                              data-form-type="other"
                              data-gramm="false"
                              data-gramm_editor="false"
                              value={passwordOf(file)}
                              onChange={(e) =>
                                handlePasswordInput(
                                  index,
                                  file,
                                  passwordOf(file),
                                  e.target.value
                                )
                              }
                              className="w-full rounded border border-gray-300 py-1 pl-2 pr-8 text-sm text-transparent caret-gray-900"
                            />
                            <div
                              aria-hidden="true"
                              className="pointer-events-none absolute inset-0 flex items-center overflow-hidden py-1 pl-2 pr-8 text-sm"
                            >
                              {passwordOf(file).length === 0 ? (
                                <span className="text-gray-400">Password</span>
                              ) : (
                                <span className="truncate tracking-wide text-primary">
                                  {maskedPassword(index, passwordOf(file))}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setVisiblePasswords((prev) => ({
                                  ...prev,
                                  [index]: !prev[index],
                                }));
                              }}
                              className="absolute inset-y-0 right-0 flex items-center px-2 text-[#858897] hover:text-primary"
                              aria-label={
                                visiblePasswords[index]
                                  ? 'Hide password'
                                  : 'Show password'
                              }
                            >
                              {visiblePasswords[index] ? (
                                <EyeSlash size={16} />
                              ) : (
                                <Eye size={16} />
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-graysecondary">
                            The password is used only to combine your files. It
                            is never stored or sent with your documents.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Control buttons */}
                <div className="mt-3 flex items-center justify-between border-t pt-3">
                  <p
                    className={`text-xs ${
                      uploadedFiles.reduce((sum, f) => sum + f.size, 0) >
                      MAX_REQUEST_UPLOAD_BYTES * 0.85
                        ? 'text-yellow-600'
                        : 'text-graysecondary'
                    }`}
                  >
                    {uploadedFiles.length} file
                    {uploadedFiles.length > 1 ? 's' : ''} • Total:{' '}
                    {formatBytes(
                      uploadedFiles.reduce((sum, f) => sum + f.size, 0)
                    )}{' '}
                    of {MAX_REQUEST_UPLOAD_MB}MB
                    {uploadedFiles.reduce((sum, f) => sum + f.size, 0) <
                      MAX_REQUEST_UPLOAD_BYTES &&
                      ' • Drop more files or click to add'}
                  </p>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={handleBrowse}
                      className="flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm"
                    >
                      <CloudArrowUp size={16} /> Add More
                    </button>
                    <button
                      type="button"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClear();
                      }}
                      className="flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm"
                    >
                      <X size={16} /> Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              {...getRootProps()}
              className={`mt-1 flex h-[200px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 ${
                isDragActive
                  ? 'border-blue-400'
                  : 'border-dashed border-gray-300'
              } ${disabled ? 'cursor-not-allowed bg-gray-100 opacity-60' : 'bg-gray-50'} px-6 py-6 text-center`}
            >
              <input
                {...getInputProps()}
                disabled={disabled}
                data-testid="document-upload-input"
              />
              <div className="flex flex-col items-center">
                <CloudArrowUp size={40} className="text-[#8B919F]" />
                <p className="text-[16px] text-graysecondary">
                  Drag files here or{' '}
                  <span className="cursor-pointer text-accent">browse</span> to
                  select files
                </p>
                <p className="text-xs text-graysecondary">
                  You can upload multiple files (PDF, JPG, PNG). Max total size{' '}
                  {MAX_REQUEST_UPLOAD_MB} MB.
                </p>
                <p className="mt-1 text-xs text-graysecondary">
                  Multiple files will be combined into a single PDF for
                  submission.
                </p>
              </div>
            </div>
          )}
          {errorMessage && (
            <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
          )}
        </>
      )}
    </div>
  );
}
